import React, { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'

export function ImpersonationBanner(): React.ReactElement {
  const { impersonation, endImpersonation } = useAuth()
  const [isEnding, setIsEnding] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(impersonation?.time_remaining || 0)

  useEffect(() => {
    if (!impersonation?.time_remaining) return

    setTimeRemaining(impersonation.time_remaining)

    const interval = setInterval(() => {
      setTimeRemaining(prev => Math.max(0, prev - 1))
    }, 1000)

    return () => clearInterval(interval)
  }, [impersonation?.time_remaining])

  const handleEnd = async () => {
    setIsEnding(true)
    await endImpersonation()
    setIsEnding(false)
  }

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (!impersonation?.active) return <></>

  return (
    <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span className="font-medium">Impersonating:</span>
            </div>
            <span className="font-semibold">{impersonation.user_name}</span>
            <span className="text-white/70">on</span>
            <span className="font-semibold">{impersonation.account_name}</span>
            <span className="text-white/70 text-sm ml-2">
              ({formatTime(timeRemaining)} remaining)
            </span>
          </div>

          <button
            type="button"
            onClick={handleEnd}
            disabled={isEnding}
            className="flex items-center gap-2 px-4 py-1.5 bg-white/20 hover:bg-white/30 disabled:bg-white/10 rounded-lg text-sm font-medium transition-colors"
          >
            {isEnding ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Ending...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                End Impersonation
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
